import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery } from 'react-query';
import Avatar from '@mui/material/Avatar';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Button, Badge, CircularProgress, TextField, IconButton, Divider, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from "react-query";
import Slide from '@mui/material/Slide';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';


import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { alpha, styled } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ChatDrawerWidth = 380;

const MessageBubble = styled(Box)(({ theme, isAdmin, isSystem }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  maxWidth: '85%',
  marginBottom: theme.spacing(1),
  ...(isSystem ? {
    backgroundColor: alpha(theme.palette.info.light, 0.1),
    color: theme.palette.text.secondary,
    borderRadius: theme.spacing(1),
    width: '90%',
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    fontSize: '0.85rem',
    textAlign: 'center',
  } : isAdmin ? {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.text.primary,
    borderTopRightRadius: theme.spacing(0.5),
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
  } : {
    backgroundColor: alpha(theme.palette.primary.main, 0.7),
    color: theme.palette.common.white,
    borderTopLeftRadius: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  })
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const MessageList = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  overflowY: 'auto',
  padding: theme.spacing(2, 0),
  backgroundColor: alpha(theme.palette.background.default, 0.3),
}));

const MessageFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
}));

const MessageDate = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  margin: theme.spacing(1, 0),
}));

const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  marginTop: '4px',
  textAlign: 'right',
}));

export default function ListFundings() {
    const [fundings, setfundings] = useState(null);
    // State for chat counts
    const [chatCounts, setChatCounts] = useState({});
    // State for chat drawer
    const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [currentChatData, setCurrentChatData] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [loadingChat, setLoadingChat] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);

    const { isLoading: isLoadingfundings, refetch: getfundings } = useQuery(
        "show-fundings",
        async () => {
          return await apiClient.get("/api/show-fundings");
        },
        {
          enabled: false,
          onSuccess: (res) => {
            if (res.data.status === 'success') {
                setfundings(res.data.fundings);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
                
                // Fetch chat counts for each funding
                res.data.fundings.forEach(funding => {
                    fetchChatCount(funding.id);
                });
            }
          },
          onError: (err) => {
            // console.log(err.response?.data || err);
          },
        }
    );

    // Function to fetch chat count for a funding
    const fetchChatCount = async (fundingId) => {
        try {
            const response = await apiClient.get(`/api/funding/chat/count/${fundingId}`);
            if (response.data.status === 'success') {
                setChatCounts(prev => ({
                    ...prev, 
                    [fundingId]: {
                        count: response.data.count,
                        chatId: response.data.chat_id
                    }
                }));
            }
        } catch (error) {
            console.error("Error fetching chat count:", error);
        }
    };

    // Function to open chat drawer
    const openChatDrawer = async (chatId) => {
        if (!chatId) return;
        
        setCurrentChatId(chatId);
        setChatDrawerOpen(true);
        setLoadingChat(true);
        
        try {
            const response = await apiClient.get(`/api/funding/chat/admin-details/${chatId}`);
            if (response.data.status === 'success') {
                setChatMessages(response.data.messages);
                setCurrentChatData(response.data.chat);
            }
        } catch (error) {
            console.error("Error fetching chat details:", error);
        } finally {
            setLoadingChat(false);
        }
    };

    // Function to close chat drawer
    const closeChatDrawer = () => {
        setChatDrawerOpen(false);
        setCurrentChatId(null);
        setCurrentChatData(null);
        setChatMessages([]);
    };

    // Scroll to bottom of messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    // Send message mutation
    const { mutate: sendMessage, isLoading: isSendingMessage } = useMutation(
        async () => {
            return await apiClient.post('/api/funding/chat/admin-message', {
                chat_id: currentChatId,
                message: newMessage
            });
        },
        {
            onSuccess: (response) => {
                // Add the new message to the list
                if (response.data.status === 'success') {
                    setChatMessages(prev => [...prev, response.data.message_details]);
                    setNewMessage('');
                }
            },
            onError: (error) => {
                console.error("Error sending message:", error);
            }
        }
    );

    const handleSendMessage = () => {
        if (newMessage.trim() && currentChatId) {
            sendMessage();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Poll for new messages when chat is open
    useEffect(() => {
        let interval;
        if (currentChatId && chatDrawerOpen) {
            interval = setInterval(async () => {
                try {
                    const response = await apiClient.get(`/api/funding/chat/admin-details/${currentChatId}`);
                    if (response.data.status === 'success') {
                        setChatMessages(response.data.messages);
                    }
                } catch (error) {
                    console.error("Error polling chat messages:", error);
                }
            }, 5000); // Poll every 5 seconds
        }
        
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [currentChatId, chatDrawerOpen]);

    const [fundingId, setfundingId] = useState('');
    const [fundingType, setfundingType] = useState('');
    const [fundingStatus, setfundingStatus] = useState('');
    const [open2, setOpen2] = React.useState(false);

    const handleClose2 = () => {
        setOpen2(false);
    };
    
    const handleToggle2 = () => {
        setOpen2(!open2);
    };

    const { isLoading: isUpdating, mutate: updatefunding } = useMutation(
        async () => {
            return await apiClient.post(`/api/update-funding`, {
            funding_id: fundingId,
            funding_status: fundingStatus,
            });
        },
        {
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
                    setTimeout(() => {
                        const event = new Event('newMessage');
                        window.dispatchEvent(event);
                    }, 1000);
                    
                    const event2 = new Event('processed');
                    window.dispatchEvent(event2);                
                    
                    // Refresh funding list after update
                    getfundings();
                }            
            },
            onError: (err) => {
                let errorMessage = err.response?.data || err ;
                localStorage.setItem('notification', JSON.stringify({message : `${errorMessage.message}`, 'type': 'error' }));
                const event = new Event('newMessage');
                window.dispatchEvent(event);
                const event4 = new Event('processed');
                window.dispatchEvent(event4);
            },
        }
    );

    useEffect(() => {
        getfundings() 
        if (isLoadingfundings) {
            const event3 = new Event('processing');
            window.dispatchEvent(event3);
        }
    }, []);

    // Group messages by date for better display
    const groupMessagesByDate = () => {
        if (!chatMessages.length) return [];
        
        let currentDate = '';
        const grouped = [];
        
        chatMessages.forEach(message => {
            const messageDate = message.date;
            
            if (messageDate !== currentDate) {
                currentDate = messageDate;
                grouped.push({ type: 'date', date: messageDate });
            }
            
            grouped.push({ type: 'message', ...message });
        });
        
        return grouped;
    };

    const getDate = (date) => new Date(date).toDateString();
    
    return (
        <div style={{marginTop: '75px'}}>
            <Container>
                <Typography mt={4} mb={2} sx={{ fontWeight: 'bold', fontSize: 20, textAlign: 'center', textTransform: 'uppercase' }} gutterBottom variant="h6" component="div">
                    {isLoadingfundings ? 'Loading fundings...' : 'List of Wallet fundinges'}
                </Typography>
                <TableContainer component={Paper}>
                    {fundings !== null &&
                    <Table sx={{ minWidth: '100%' }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">Gateway</TableCell>
                                <TableCell align="right">Transaction ID</TableCell>
                                <TableCell align="right">Status</TableCell>
                                <TableCell align="center">Chat</TableCell>
                                <TableCell align="right">Edit</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fundings.map((funding) => (
                                <TableRow
                                    key={funding.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {funding.user.name}
                                    </TableCell>
                                    <TableCell align="right">{funding.user.email}</TableCell>
                                    <TableCell align="right">{funding.amount}</TableCell>
                                    <TableCell align="right">{funding.gateway}</TableCell>
                                    <TableCell align="right">{funding.transaction_id}</TableCell>
                                    <TableCell align="right">{funding.status}</TableCell>
                                    <TableCell align="center">
                                        {chatCounts[funding.id] ? (
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                startIcon={<ChatIcon />}
                                                onClick={() => openChatDrawer(chatCounts[funding.id].chatId)}
                                                disabled={!chatCounts[funding.id].chatId}
                                            >
                                                <Badge 
                                                    badgeContent={chatCounts[funding.id].count} 
                                                    color="primary"
                                                    sx={{ mr: 1 }}
                                                >
                                                    Chat
                                                </Badge>
                                            </Button>
                                        ) : (
                                            <Chip label="No Chat" size="small" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button 
                                            onClick={() => {handleToggle2(); setfundingId(funding.id); setfundingType(funding)}} 
                                            startIcon={<EditIcon />}
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    }
                </TableContainer>
            </Container>

            {/* Status Update Dialog */}
            <Dialog
                open={open2}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose2}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle sx={{textAlign: 'center'}}>{"UPDATE STATUS"}</DialogTitle>
                <DialogContent>
                    <Box px={1} sx={{display: 'flex', justifyContent: 'space-evenly'}}>
                        <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                            <InputLabel htmlFor="status">status</InputLabel>
                            <Select
                                onChange={(e) => setfundingStatus(e.target.value)}
                                labelId="status"
                                id="status"
                                name="status"
                                label='status'
                            >
                                <MenuItem selected value=''>
                                    select status
                                </MenuItem> 
                                <MenuItem value="pending">pending</MenuItem>
                                <MenuItem value={fundingType.status === 'processing'? 'successful' : 'approved'}>Approved</MenuItem>
                                <MenuItem value="declined">Declined</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>          
                </DialogContent>
                
                <DialogActions>
                    <Button onClick={()=>{updatefunding(); handleClose2();}} color='error'>
                        {isUpdating ? 'Updating...' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Chat Drawer */}
            <Drawer
                anchor="right"
                open={chatDrawerOpen}
                onClose={closeChatDrawer}
                sx={{
                    zIndex: 50000,
                    width: ChatDrawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: ChatDrawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
            >
                {/* Chat Header */}
                <MessageHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                            {currentChatData?.user?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {currentChatData?.user?.name || 'User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {currentChatData?.payment_method || 'Payment Method'} Transaction
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={closeChatDrawer}>
                        <CloseIcon />
                    </IconButton>
                </MessageHeader>

                {/* Transaction Info */}
                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Transaction #{currentChatData?.funding?.id}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Amount:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            ${parseFloat(currentChatData?.funding?.amount || 0).toFixed(2)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip 
                            label={currentChatData?.funding?.status || 'Pending'} 
                            size="small" 
                            color={
                                currentChatData?.funding?.status === 'successful' || 
                                currentChatData?.funding?.status === 'approved' 
                                    ? 'success' 
                                    : currentChatData?.funding?.status === 'declined' 
                                        ? 'error' 
                                        : 'primary'
                            } 
                            variant="outlined" 
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Date:</Typography>
                        <Typography variant="body2">
                            {currentChatData?.funding?.created_at || 'N/A'}
                        </Typography>
                    </Box>
                </Box>
                
                <Divider />
                
                {/* Chat Messages */}
                <MessageList>
                    {loadingChat ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : chatMessages.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                                No messages yet
                            </Typography>
                        </Box>
                    ) : (
                        groupMessagesByDate().map((item, index) => (
                            item.type === 'date' ? (
                                <MessageDate key={`date-${index}`}>{item.date}</MessageDate>
                            ) : (
                                <MessageBubble 
                                    key={`msg-${item.id}`} 
                                    isAdmin={item.sender_id === currentChatData?.user?.id ? false : true} 
                                    isSystem={item.is_system}
                                >
                                    {item.is_system ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium" fontStyle="italic">
                                                    {item.message}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <>
                                            <Typography variant="body2">{item.message}</Typography>
                                            <MessageTime>{item.time}</MessageTime>
                                        </>
                                    )}
                                </MessageBubble>
                            )
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </MessageList>
                
                {/* Chat Input */}
                <MessageFooter>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type a message..."
                        size="small"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isSendingMessage}
                        InputProps={{
                            endAdornment: (
                                <IconButton 
                                    color="primary" 
                                    onClick={handleSendMessage}
                                    disabled={!newMessage.trim() || isSendingMessage}
                                >
                                    {isSendingMessage ? <CircularProgress size={20} /> : <SendIcon />}
                                </IconButton>
                            ),
                        }}
                    />
                </MessageFooter>
            </Drawer>
        </div>
    );
}